import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory, useParams } from "react-router-dom";
import CompanyViewDetails from "../../../../../components/Company/CompanyViewDetails";
import Breadcrumbs from "../../../../../components/BreadCrumbs";
import TruncatedWithTooltip from "../../../../../components/TruncatedWithTooltip";
import messages from "../../../../../assets/locale/messages";
import { rejectCompany } from "../../../../../network/apis/companies";
import { getCompanyDetailsRequest } from "../../../../../store/RegistrationReq/actions";
import { showErrorMsg, showSuccessMsg } from "../../../../../utils/Helpers";
import { isSuperAdmin } from "../../../../../utils/Permissions";
import { ROUTE_PATHS } from "../../../../../utils/RoutesPaths";
import RejectReasonModal from "../../../../../components/modals/RejectReasonModal";
import ApproveAssignPlanModal from "../components/ApproveAssignPlanModal";

const CompanyViewDetailsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const companyDetails = useSelector(
    (state) => state.companiesRegistration.companyDetails
  );
  const { shared, registrationReq } = messages[lang];
  const [rejectTarget, setRejectTarget] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);

  const displayName =
    lang === "ar"
      ? companyDetails?.name_ar || companyDetails?.name_en
      : companyDetails?.name_en || companyDetails?.name_ar;

  if (!isSuperAdmin()) {
    return <Redirect to={ROUTE_PATHS.events} />;
  }

  const handleRejectClick = (company) => {
    setRejectTarget({
      id: company.id,
      name:
        lang === "ar"
          ? company.name_ar || company.name_en
          : company.name_en,
    });
  };

  const handleReject = async (companyId, reason) => {
    try {
      await rejectCompany(companyId, reason);
      showSuccessMsg("rejectRequest");
      history.push(ROUTE_PATHS.registrationRequests);
    } catch (error) {
      const msg = error.response?.data?.message;
      if (msg) showErrorMsg(msg);
      throw error;
    }
  };

  const rejectName = rejectTarget?.name || "—";
  const rejectDescParts = registrationReq.rejectModalDescription.split("{name}");

  return (
    <div className="registration-req-page">
      <div className="mt-3">
        <Breadcrumbs
          list={[
            { path: ROUTE_PATHS.companiesList, label: shared.companies },
            {
              path: ROUTE_PATHS.registrationRequests,
              label: registrationReq.listBreadcrumb,
            },
            {
              path: "",
              label: (
                <TruncatedWithTooltip
                  title={displayName || ""}
                  style={{ maxWidth: 320, display: "inline-block" }}
                >
                  <span>{displayName || id || "—"}</span>
                </TruncatedWithTooltip>
              ),
            },
          ]}
          BreadCrumbHasBackIcon={false}
        />
      </div>

      <CompanyViewDetails
        onApprove={() => setApproveOpen(true)}
        onRejectClick={handleRejectClick}
      />

      <ApproveAssignPlanModal
        open={approveOpen}
        companyId={companyDetails?.id}
        companyName={displayName}
        onClose={() => setApproveOpen(false)}
        onApproved={() => {
          setApproveOpen(false);
          // The page now shows the approved company, with no Approve / Reject.
          dispatch(getCompanyDetailsRequest({ id: companyDetails.id, force: true }));
        }}
      />

      <RejectReasonModal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={async (reason) => {
          if (rejectTarget) {
            await handleReject(rejectTarget.id, reason);
          }
        }}
        title={registrationReq.rejectModalTitle}
        modalClassName="registration-req-reject-modal"
        description={
          <>
            {rejectDescParts[0]}
            <strong>{rejectName}</strong>
            {rejectDescParts[1] ?? ""}
          </>
        }
        reasonLabel={registrationReq.rejectionReasonLabel}
        reasonPlaceholder={registrationReq.rejectionReasonPlaceholder}
        confirmLabel={registrationReq.confirmRejection}
        cancelLabel={shared.cancel}
        minReasonLength={3}
        requiredErrorMessage={registrationReq.required}
        minLengthErrorMessage={registrationReq.rejectionReasonMinLength}
        reasonInputId="reject-registration-reason-details"
      />
    </div>
  );
};

export default CompanyViewDetailsPage;
